<script lang="ts">
  import { t } from '$core/i18n';
  import {
    type SessionStore,
    remainingFocusMs,
    remainingBreakMs,
    elapsedFocusMs
  } from '$features/session';
  import { Button } from '$ui/primitives/Button';
  import { Icon } from '$ui/primitives/Icon';
  import type { PreferencesStore } from '$core/preferences';

  import { fade } from 'svelte/transition';
  
  let { store, prefs }: { store: SessionStore; prefs: PreferencesStore } = $props();
  let isFullscreen = $state(false);

  function toggleFullscreen() {
    isFullscreen = !isFullscreen;
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && isFullscreen) {
      isFullscreen = false;
    }
  }

  $effect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  });

  // Bloquear scroll del body en pantalla completa
  $effect(() => {
    if (typeof document === 'undefined') return;
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  });

  let nowTick = $state(Date.now());
  $effect(() => {
    const id = window.setInterval(() => { nowTick = Date.now(); }, 250);
    return () => window.clearInterval(id);
  });

  function isBreakState(state: string): boolean {
    return state === 'break_running' || state === 'break_paused';
  }

  let isBreak = $derived(isBreakState(store.macro.state));
  let isOpenEnded = $derived(store.macro.openEnded && !isBreak);

  let remainingMs = $derived.by(() => {
    void nowTick;
    if (store.macro.state === 'inactive') return store.macro.focusDurationMs;
    if (isBreak) return remainingBreakMs(store.macro);
    if (isOpenEnded) return 0;
    return remainingFocusMs(store.macro);
  });

  let totalMs = $derived(
    isBreak ? store.macro.breakDurationMs : store.macro.focusDurationMs
  );

  let elapsedMs = $derived.by(() => {
    void nowTick;
    if (store.macro.state === 'inactive') return 0;
    if (isOpenEnded) return elapsedFocusMs(store.macro);
    return Math.max(0, totalMs - remainingMs);
  });

  let progressPct = $derived(
    isOpenEnded || totalMs === 0
      ? 0
      : Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100))
  );

  let displayMs = $derived(isOpenEnded ? elapsedMs : remainingMs);
  let minutes = $derived(Math.floor(displayMs / 60000));
  let seconds = $derived(Math.floor((displayMs % 60000) / 1000));
  let formattedTime = $derived(
    `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  );

  let isRunning = $derived(
    store.macro.state === 'focus_running' || store.macro.state === 'break_running'
  );
  let isPaused = $derived(
    store.macro.state === 'focus_paused' || store.macro.state === 'break_paused'
  );
  let sessionActive = $derived(isRunning || isPaused);

  // Estado textual del sistema (Nielsen #1 / F6 §33 / F10 §1.1)
  let statusLabel = $derived.by(() => {
    switch (store.macro.state) {
      case 'focus_running':
        return t('pomodoro.statusFocusRunning');
      case 'focus_paused':
        return t('pomodoro.statusFocusPaused');
      case 'break_running':
        return t('pomodoro.statusBreakRunning');
      case 'break_paused':
        return t('pomodoro.statusBreakPaused');
      case 'pending_finish':
        return t('pomodoro.statusPendingFinish');
      case 'finished':
        return t('pomodoro.statusFinished');
      case 'discarded':
        return t('pomodoro.statusDiscarded');
      default:
        return t('pomodoro.statusIdle');
    }
  });

  // Texto de progreso: "12 min restantes · 48% completado" (F6 §18, F10 §1.1).
  // Sólo se muestra cuando hay una sesión activa (running/paused) y no es
  // open-ended. En idle (problema aún no arrancado) se omite el % porque
  // "0% complete" resulta semánticamente confuso — sólo se muestran los
  // minutos restantes del bloque.
  let progressText = $derived.by(() => {
    if (!sessionActive || isOpenEnded || totalMs === 0) return '';
    const remainingMin = Math.max(0, Math.ceil(remainingMs / 60000));
    const remaining = t('pomodoro.timeRemaining', { min: remainingMin });
    if (store.problem.state === 'idle') return remaining;
    const pct = Math.round(progressPct);
    return `${remaining} · ${t('pomodoro.progressPct', { pct })}`;
  });

  // --- Reading timer ---
  let readingRunning = $state(false);
  let readingStartedAt = $state<number | null>(null);
  let readingLiveMs = $state(0);

  $effect(() => {
    if (!readingRunning) readingLiveMs = store.macro.readingAccumulatedMs;
  });

  $effect(() => {
    if (!readingRunning) return;
    const id = window.setInterval(() => {
      const live = readingStartedAt ? Date.now() - readingStartedAt : 0;
      readingLiveMs = store.macro.readingAccumulatedMs + live;
    }, 500);
    return () => window.clearInterval(id);
  });

  let readingDisplay = $derived.by(() => {
    const m = Math.floor(readingLiveMs / 60000);
    const s = Math.floor((readingLiveMs % 60000) / 1000);
    return `${m}:${String(s).padStart(2, '0')}`;
  });

  function toggleReading() {
    if (readingRunning) {
      const delta = readingStartedAt ? Date.now() - readingStartedAt : 0;
      if (delta > 0) store.registerReading(delta);
      readingRunning = false;
      readingStartedAt = null;
    } else {
      readingStartedAt = Date.now();
      readingRunning = true;
    }
  }

  // --- Reset with loss-aversion ---
  let showResetConfirm = $state(false);
  let resetCounts = $state<{ problems: number; conjectures: number; focusMs: number } | null>(null);

  async function askReset() {
    resetCounts = await store.countActiveSessionArtifacts();
    showResetConfirm = true;
  }
  function cancelReset() {
    showResetConfirm = false;
    resetCounts = null;
  }
  async function confirmReset() {
    showResetConfirm = false;
    resetCounts = null;
    readingRunning = false;
    readingStartedAt = null;
    await store.resetActiveSession();
  }

  let resetMessage = $derived.by(() => {
    if (!resetCounts) return '';
    const parts: string[] = [];
    if (resetCounts.problems > 0) {
      parts.push(
        resetCounts.problems === 1
          ? t('pomodoro.resetProblems', { n: resetCounts.problems })
          : t('pomodoro.resetProblemsPlural', { n: resetCounts.problems })
      );
    }
    const focusMin = Math.round(resetCounts.focusMs / 60000);
    if (focusMin > 0) parts.push(t('pomodoro.resetFocus', { min: focusMin }));
    if (resetCounts.conjectures > 0) {
      parts.push(
        resetCounts.conjectures === 1
          ? t('pomodoro.resetConjectures', { n: resetCounts.conjectures })
          : t('pomodoro.resetConjecturesPlural', { n: resetCounts.conjectures })
      );
    }
    if (parts.length === 0) return t('pomodoro.resetNothingToLose');
    const list =
      parts.length === 1
        ? (parts[0] ?? '')
        : parts.slice(0, -1).join(', ') +
          t('pomodoro.resetConjunction') +
          (parts[parts.length - 1] ?? '');
    return t('pomodoro.resetIntro', { list });
  });

  function togglePause() {
    if (isRunning) store.pauseSession();
    else if (isPaused) store.resumeSession();
  }
</script>

{#if isFullscreen}
  <div class="fullscreen-backdrop" transition:fade={{ duration: 200 }}></div>
{/if}

<section class="pomodoro-panel" class:fullscreen={isFullscreen} data-break={isBreak || undefined}>
  <header class="panel-header">
    <h2 class="panel-title">Session</h2>
    <button class="icon-btn" onclick={toggleFullscreen} aria-label="Toggle fullscreen" title="Toggle fullscreen">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        {#if isFullscreen}
          <!-- Minimize: flechas apuntando hacia adentro -->
          <polyline points="4 14 10 14 10 20"></polyline>
          <polyline points="20 10 14 10 14 4"></polyline>
          <line x1="14" y1="10" x2="21" y2="3"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        {:else}
          <!-- Maximize: flechas apuntando hacia afuera -->
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        {/if}
      </svg>
    </button>
  </header>

  <div class="circle-wrap">
    <svg class="ring" viewBox="0 0 100 100" aria-hidden="true">
      <circle class="ring-track" cx="50" cy="50" r="46" />
      <circle
        class="ring-progress"
        cx="50" cy="50" r="46"
        pathLength="100"
        style="stroke-dashoffset: {100 - progressPct}"
      />
    </svg>
    <div class="circle-content">
      <span class="time" role="timer" aria-live="off">
        {formattedTime}{#if isOpenEnded}<span class="open-tag">∞</span>{/if}
      </span>
    </div>
  </div>

  <span class="status-label" role="status" aria-live="polite">{statusLabel}</span>
  {#if progressText}
    <span class="progress-text" aria-live="polite">{progressText}</span>
  {/if}

  <div class="controls">
    <div class="controls-row">
      {#if sessionActive}
        <button
          type="button"
          class="icon-btn icon-btn--filled"
          onclick={togglePause}
          aria-label={isRunning ? t('pomodoro.controlPause') : t('pomodoro.controlResume')}
          title={isRunning ? t('pomodoro.controlPause') : t('pomodoro.controlResume')}
        >
          <Icon name={isRunning ? 'pause' : 'play'} size={18} />
        </button>
      {/if}
      <button
        type="button"
        class="icon-btn icon-btn--outline"
        onclick={() => store.finishSession()}
        aria-label={t('pomodoro.controlFinish')}
        title={t('pomodoro.controlFinish')}
      >
        <Icon name="square" size={18} />
      </button>
      <button
        type="button"
        class="icon-btn icon-btn--danger"
        onclick={() => void askReset()}
        aria-label={t('pomodoro.controlReset')}
        title={t('pomodoro.controlReset')}
      >
        <Icon name="rotate-ccw" size={18} />
      </button>
      {#if isRunning && prefs.prefs.showDistractions}
        <button
          type="button"
          class="icon-btn icon-btn--ghost"
          onclick={() => store.registerDistraction()}
          aria-label={t('pomodoro.controlDistraction')}
          title={t('pomodoro.controlDistraction')}
        >
          <Icon name="alert-triangle" size={18} />
          {#if store.macro.distractions > 0}
            <span class="badge">{store.macro.distractions}</span>
          {/if}
        </button>
      {/if}
      {#if prefs.prefs.showReadingTime}
        <button
          type="button"
          class="icon-btn icon-btn--ghost {readingRunning ? 'is-active' : ''}"
          onclick={toggleReading}
          aria-label={readingRunning ? t('pomodoro.controlReadingPause') : t('pomodoro.controlReadingStart')}
          title={readingRunning ? t('pomodoro.controlReadingPause') : t('pomodoro.controlReadingStart')}
        >
          <Icon name={readingRunning ? 'square' : 'book-open'} size={18} />
        </button>
      {/if}
    </div>
  </div>

  {#if sessionActive && prefs.prefs.showReadingTime}
    <div class="reading-footer" aria-live="polite">
      <Icon name="book-open" size={12} />
      <span class="reading-label">{t('pomodoro.readingFooterLabel')}</span>
      <span class="reading-value">{readingDisplay}</span>
    </div>
  {/if}
</section>

{#if showResetConfirm && resetCounts}
  <div class="reset-overlay" role="dialog" aria-modal="true" aria-label={t('pomodoro.resetTitle')}>
    <div class="reset-modal">
      <h3>{t('pomodoro.resetTitle')}</h3>
      <p>{resetMessage}</p>
      <p class="hint">{t('pomodoro.resetHint')}</p>
      <div class="reset-actions">
        <Button variant="plain" size="sm" onclick={cancelReset}>{t('pomodoro.resetCancel')}</Button>
        <Button variant="danger" size="sm" onclick={() => void confirmReset()}>{t('pomodoro.resetConfirm')}</Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .panel-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-4);
  }
  .panel-header .panel-title {
    flex: 1;
    text-align: center;
  }
  .panel-header .icon-btn {
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-sm);
    transition: color var(--dur-fast) var(--ease-out);
  }
  .panel-header .icon-btn:hover {
    color: var(--color-text-primary);
  }

  /* Modo Pantalla Completa */
  .fullscreen-backdrop {
    position: fixed;
    inset: 0;
    background: var(--color-bg);
    z-index: calc(var(--z-modal) - 1);
  }

  .pomodoro-panel.fullscreen {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    background: var(--color-bg);
    border-radius: 0;
    border: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-8);
    animation: expandPanel var(--dur-slow) var(--ease-out) forwards;
  }

  @keyframes expandPanel {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }

  .pomodoro-panel.fullscreen .panel-header {
    position: absolute;
    top: var(--space-6);
    right: var(--space-6);
    width: auto;
    margin: 0;
  }
  
  .pomodoro-panel.fullscreen .panel-title {
    display: none;
  }

  /* Ocultar el círculo en pantalla completa */
  .pomodoro-panel.fullscreen .ring {
    display: none;
  }
  
  .pomodoro-panel.fullscreen .circle-wrap {
    width: 100%;
    height: auto;
    margin: 0;
  }

  .pomodoro-panel.fullscreen .time {
    font-size: clamp(8rem, 18vw, 20rem);
    text-align: center;
    line-height: 1;
  }

  .pomodoro-panel.fullscreen .controls-row button {
    opacity: 0.15; /* Baja opacidad */
    transition: opacity var(--dur-fast) var(--ease-out);
  }
  
  .pomodoro-panel.fullscreen .controls-row button:hover {
    opacity: 1; /* Alta opacidad al pasar el cursor */
  }
  .panel-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .pomodoro-panel {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .circle-wrap {
    position: relative;
    width: 130px;
    height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  .ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  .ring-track {
    fill: none;
    stroke: var(--color-border);
    stroke-width: 4;
  }

  .ring-progress {
    fill: none;
    stroke: var(--color-brand);
    stroke-width: 4;
    stroke-dasharray: 100;
    stroke-dashoffset: 100;
    stroke-linecap: butt;
    /* Spring en lugar de linear — el avance del ring se siente orgánico
       y settle suave en cada tick en lugar de mecánico. */
    transition: stroke-dashoffset var(--dur-base) var(--spring-soft);
  }

  .pomodoro-panel[data-break] .ring-progress {
    stroke: var(--color-success, #7FA88D);
  }

  .circle-content {
    display: flex;
    align-items: baseline;
    gap: 2px;
    z-index: 1;
  }

  .time {
    font-family: var(--font-mono, monospace);
    font-weight: 700;
    font-size: 1.45rem;
    letter-spacing: -0.03em;
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  .pomodoro-panel[data-break] .time {
    color: var(--color-success, #7FA88D);
  }

  .open-tag {
    font-size: 1rem;
    color: var(--color-brand);
  }

  /* Estado textual del sistema (Nielsen #1) */
  .status-label {
    display: block;
    text-align: center;
    font-family: var(--font-body, sans-serif);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
    margin-top: calc(var(--space-2) * -1);
  }

  .pomodoro-panel[data-break] .status-label {
    color: var(--color-success, #7FA88D);
  }

  /* Texto de progreso (F6 §18, F10 §1.1). `text-muted` en lugar de
     `text-faint` para cumplir WCAG AA (ratio 8.26:1 vs 3.41:1 sobre
     surface, F6 §21, F8 §3.3, F9 §3.1, F10 §2.4). */
  .progress-text {
    display: block;
    text-align: center;
    font-family: var(--font-mono, monospace);
    font-size: var(--text-caption);
    letter-spacing: 0.02em;
    color: var(--color-text-muted);
    margin-top: calc(var(--space-1) * -1);
    font-variant-numeric: tabular-nums;
  }

  /* Botones icon-only cuadrados en fila — sin texto. `title` + aria-label
     para accesibilidad (F6 §31, F8 §6). */
  .controls {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    width: 100%;
    margin-top: var(--space-1);
  }

  .controls-row {
    display: flex;
    gap: var(--space-2);
    justify-content: center;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .reading-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding-top: var(--space-2);
    border-top: var(--border-width) solid var(--color-border);
    margin-top: var(--space-2);
    color: var(--color-text-muted);
  }

  .reading-label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .reading-value {
    font-family: var(--font-mono, monospace);
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;
    color: var(--color-text-primary);
  }

  .icon-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: var(--border-width) solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition:
      background var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out),
      transform var(--dur-fast) var(--spring-snappy);
  }

  .icon-btn:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  .icon-btn:not(:disabled):active {
    transform: scale(0.94);
  }

  .icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Filled: CTA principal (Pause) */
  .icon-btn--filled {
    background: var(--color-brand);
    border-color: var(--color-brand);
    color: var(--color-brand-fg);
  }
  .icon-btn--filled:hover:not(:disabled) {
    background: var(--color-brand-hover);
    border-color: var(--color-brand-hover);
  }

  /* Outline: acción secundaria (Finish) */
  .icon-btn--outline {
    background: transparent;
    border-color: var(--color-border-strong);
    color: var(--color-text-primary);
  }
  .icon-btn--outline:hover:not(:disabled) {
    background: var(--color-surface-hover);
    border-color: var(--color-text-muted);
  }

  /* Danger: destructivo (Reset) */
  .icon-btn--danger {
    background: transparent;
    border-color: transparent;
    color: var(--color-danger);
  }
  .icon-btn--danger:hover:not(:disabled) {
    background: var(--color-danger-tint);
    border-color: var(--color-danger);
  }

  /* Ghost: acciones menores (Distraction, Reading) */
  .icon-btn--ghost {
    background: transparent;
    border-color: transparent;
    color: var(--color-text-secondary);
  }
  .icon-btn--ghost:hover:not(:disabled) {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
  }

  /* Toggle activo (Reading corriendo) */
  .icon-btn--ghost.is-active {
    background: var(--color-brand-tint);
    border-color: var(--color-brand);
    color: var(--color-brand);
  }

  .badge {
    margin-left: auto;
    min-width: 20px;
    height: 20px;
    padding: 0 var(--space-1);
    border-radius: var(--radius-full, 9999px);
    background: var(--color-brand);
    color: var(--color-brand-fg);
    font-size: 0.7rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .reset-overlay {
    position: fixed;
    inset: 0;
    background: var(--color-scrim);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-modal, 50);
    padding: var(--space-4);
  }

  .reset-modal {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--space-5);
    max-width: 420px;
    width: 100%;
    box-shadow: var(--shadow-lg);
  }

  .reset-modal h3 {
    margin: 0 0 var(--space-3) 0;
    font-size: 1.25rem;
    color: var(--color-text-primary);
  }

  .reset-modal p {
    margin: 0 0 var(--space-2) 0;
    color: var(--color-text-secondary);
  }

  .reset-modal .hint {
    font-size: 0.85rem;
    color: var(--color-text-muted);
    margin-bottom: var(--space-4);
  }

  .reset-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
  }
</style>